import { useLocalSearchParams } from 'expo-router'
import { ChevronRight } from 'lucide-react-native'
import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import type { Tag } from '@/types/tags'
import { CardLink } from '@/ui/CardLink'

interface TagCardProps {
    tag: Tag
}

export function TagCard({ tag }: TagCardProps) {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const theme = useTheme()
    const createdDate = tag.created ? new Date(tag.created) : null

    return (
        <CardLink
            href={`/${params.orgSlug}/settings/tags/${tag.id}`}
            accessibilityLabel={`Edit tag ${tag.name}`}
        >
            <View style={styles.cardContent}>
                <View style={[styles.colorIndicator, { backgroundColor: tag.color }]} />

                <View style={styles.tagInfo}>
                    <View style={styles.header}>
                        <Text variant="titleMedium" style={styles.name} numberOfLines={2}>
                            {tag.name}
                        </Text>
                        {tag.inactive && (
                            <Text variant="labelSmall" style={styles.inactiveTag}>
                                Inactive
                            </Text>
                        )}
                    </View>

                    {tag.description && tag.description.trim().length > 0 && (
                        <Text variant="bodyMedium" style={styles.description} numberOfLines={2}>
                            {tag.description}
                        </Text>
                    )}

                    {createdDate && (
                        <Text variant="bodySmall" style={styles.metadata}>
                            Created {createdDate.toLocaleDateString()}
                        </Text>
                    )}
                </View>

                <ChevronRight size={20} color={theme.colors.onSurfaceVariant} />
            </View>
        </CardLink>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    colorIndicator: {
        width: 40,
        height: 40,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: '#e0e0e0',
        flexShrink: 0,
    },
    tagInfo: {
        flex: 1,
        gap: 4,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    name: {
        fontWeight: '600',
        flex: 1,
        textTransform: 'lowercase',
    },
    inactiveTag: {
        backgroundColor: '#fce4ec',
        color: '#ad1457',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 12,
        overflow: 'hidden',
        flexShrink: 0,
    },
    description: {
        color: '#4a4a4a',
    },
    metadata: {
        color: '#777',
    },
})
